// Row contracts of Knowledge pages (M7-02; 02 §Knowledge). Same conventions as `work-rows.ts`:
// keys are column names, nullability is the column's, enums are the CHECK's;
// `contract-drift.test.ts` binds each to its table.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import { ActorKind, PageKind, PageUploadStatus } from "./enums";
import { Timestamp, Uuid } from "./primitives";
import { BlockNoteDoc } from "./work-rows";

export const Page = z
  .object({
    company_id: Uuid,
    id: Uuid,
    space_id: Uuid,
    parent_page_id: Uuid.nullable(),
    kind: PageKind,
    title: z.string(),
    icon: z.string().nullable(),
    body: BlockNoteDoc,
    body_text: z.string(),
    version: z.int().min(1),
    position: z.int(),
    restricted: z.boolean(),
    /** KB-UPLOAD-2: read only for everyone but its managers and `editor` shares. */
    read_only: z.boolean(),
    promoted_from_memory_ref: z.string().nullable(),
    thread_id: Uuid.nullable(),
    created_by_membership_id: Uuid.nullable(),
    archived_at: Timestamp.nullable(),
    deleted_at: Timestamp.nullable(),
    ...ActorShortColumns.shape,
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "Page" });
export type Page = z.infer<typeof Page>;

/**
 * KB-PDF-UPLOAD: the document a page was made from and its text extraction (`page_upload`; the
 * object keys stay server-side: the bytes leave through `pages.uploadContent` only).
 */
export const PageUpload = z
  .object({
    company_id: Uuid,
    page_id: Uuid,
    file_name: z.string(),
    mime: z.string(),
    bytes: z.int().min(1),
    status: PageUploadStatus,
    error: z.string().nullable(),
    markdown_chars: z.int().nullable(),
    source_pages: z.int().nullable(),
    truncated: z.boolean(),
    extracted_at: Timestamp.nullable(),
    uploader_membership_id: Uuid.nullable(),
    // UPLOAD-ORIGINAL: Replace file adds a revision; Remove original keeps the doc's text.
    revision: z.int().min(1),
    original_uploaded_at: Timestamp.nullable(),
    original_removed_at: Timestamp.nullable(),
    ...ActorShortColumns.shape,
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "PageUpload" });
export type PageUpload = z.infer<typeof PageUpload>;

export const PageVersion = z
  .object({
    company_id: Uuid,
    page_id: Uuid,
    version: z.int().min(1),
    title: z.string(),
    body: BlockNoteDoc,
    body_text: z.string(),
    author_membership_id: Uuid.nullable(),
    author_kind: ActorKind,
    restored_from_version: z.int().min(1).nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "PageVersion" });
export type PageVersion = z.infer<typeof PageVersion>;
